import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { Button, Form, Table, Alert, Badge } from "react-bootstrap";
import axios from "../../axios/axiosConfig";

/**
 * AdminFieldSearch - Admin-only component for searching database field names (keys)
 * 
 * Allows admins to search for field names across all API tables and see which tables contain
 * specific fields. Useful for finding where data is stored and understanding the schema.
 */
function AdminFieldSearch() {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allFields, setAllFields] = useState({});
  const [dataFetched, setDataFetched] = useState(false);
  const [loadedApis, setLoadedApis] = useState({}); // Track which APIs loaded successfully
  const apiUrls = useSelector(selectApiUrls);

  // Define which APIs to search - wrapped in useMemo to prevent recreating on every render
  const selectedApiUrls = useMemo(() => ({
    generalProject: apiUrls.generalProject,
    generalStation: apiUrls.generalStation,
    soldToShipTo: apiUrls.soldToShipTo,
    priceBreakdown: apiUrls.priceBreakdown,
    ownersManual: apiUrls.ownersManual,
    hcRequirements: apiUrls.hcRequirements,
    skidConstruction: apiUrls.skidConstruction,
    pumpManifoldPiping: apiUrls.pumpManifoldPiping,
    stationEnclosure: apiUrls.stationEnclosure,
    dropPipeComments: apiUrls.dropPipeComments,
    dropPipes: apiUrls.dropPipes,
    levelControls: apiUrls.levelControls,
    fillSources: apiUrls.fillSources,
    pumps: apiUrls.pumps,
    pumpDetails: apiUrls.pumpDetails,
    waterTreatment: apiUrls.waterTreatment,
    inletScreen: apiUrls.inletScreen,
    inletStrainer: apiUrls.inletStrainer,
    dischargeFilter: apiUrls.dischargeFilter,
    dischargeStrainer: apiUrls.dischargeStrainer,
    roSystem: apiUrls.roSystem,
    uvSystem: apiUrls.uvSystem,
    fertigation: apiUrls.fertigation,
    skyHarvester: apiUrls.skyHarvester,
    shipLoose: apiUrls.shipLoose,
    unregulatedPower: apiUrls.unregulatedPower,
    unregulatedBreakers: apiUrls.unregulatedBreakers,
    regulatedPower: apiUrls.regulatedPower,
    regulatedBreakers: apiUrls.regulatedBreakers,
    sequenceOfOperations: apiUrls.sequenceOfOperations,
    electricalOptions: apiUrls.electricalOptions,
    telemetry: apiUrls.telemetry,
    telemetryAccessories: apiUrls.telemetryAccessories,
    controlLogic: apiUrls.controlLogicOnly,
  }), [apiUrls]);

  // Format API names for display
  const formatApiName = (name) =>
    name.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

  // Format field names for display
  const formatFieldName = (name) =>
    name
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/([a-z\d])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());

  // Fetch field names from all APIs
  const fetchFieldNames = async () => {
    setLoading(true);
    setError(null);
    
    const fieldsMap = {};
    const fetchPromises = [];
    const apiLoadStatus = {};

    Object.entries(selectedApiUrls).forEach(([apiName, url]) => {
      if (!url) {
        apiLoadStatus[apiName] = { status: 'skipped', error: 'No URL configured' };
        return;
      }

      const fetchPromise = axios
        .get(url)
        .then((response) => {
          const data = response.data;
          
          // Extract field names from the first record (if data exists)
          if (Array.isArray(data) && data.length > 0) {
            const fieldNames = Object.keys(data[0]);
            fieldsMap[apiName] = fieldNames;
            apiLoadStatus[apiName] = { status: 'success', count: fieldNames.length };
          } else if (typeof data === 'object' && data !== null) {
            const fieldNames = Object.keys(data);
            fieldsMap[apiName] = fieldNames;
            apiLoadStatus[apiName] = { status: 'success', count: fieldNames.length };
          } else {
            fieldsMap[apiName] = [];
            apiLoadStatus[apiName] = { status: 'empty', error: 'No data returned' };
          }
        })
        .catch((err) => {
          console.error(`Error fetching ${apiName}:`, err);
          fieldsMap[apiName] = [];
          apiLoadStatus[apiName] = { 
            status: 'error', 
            error: err.response?.statusText || err.message || 'Failed to load'
          };
        });

      fetchPromises.push(fetchPromise);
    });

    try {
      await Promise.all(fetchPromises);
      setAllFields(fieldsMap);
      setLoadedApis(apiLoadStatus);
      setDataFetched(true);
    } catch (err) {
      setError("Failed to fetch field data from APIs");
      console.error("Error fetching field names:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter fields based on search term
  const filteredResults = useMemo(() => {
    if (!searchTerm || !dataFetched) return [];

    const results = [];
    const lowerSearchTerm = searchTerm.toLowerCase();

    Object.entries(allFields).forEach(([apiName, fields]) => {
      fields.forEach((fieldName) => {
        if (fieldName.toLowerCase().includes(lowerSearchTerm)) {
          results.push({
            apiName,
            fieldName,
            formattedApiName: formatApiName(apiName),
            formattedFieldName: formatFieldName(fieldName),
          });
        }
      });
    });

    // Sort by API name, then field name
    return results.sort((a, b) => {
      if (a.apiName !== b.apiName) {
        return a.formattedApiName.localeCompare(b.formattedApiName);
      }
      return a.formattedFieldName.localeCompare(b.formattedFieldName);
    });
  }, [searchTerm, allFields, dataFetched]);

  // Calculate stats
  const loadStats = useMemo(() => {
    const totalApis = Object.keys(selectedApiUrls).length;
    const successCount = Object.values(loadedApis).filter(api => api.status === 'success').length;
    const errorCount = Object.values(loadedApis).filter(api => api.status === 'error').length;
    const emptyCount = Object.values(loadedApis).filter(api => api.status === 'empty').length;
    const totalFields = Object.values(allFields).reduce((sum, fields) => sum + fields.length, 0);
    
    return { totalApis, successCount, errorCount, emptyCount, totalFields };
  }, [loadedApis, allFields, selectedApiUrls]);

  // Highlight matching text
  const highlightText = (text, search) => {
    if (!search) return text;
    
    const parts = text.split(new RegExp(`(${search})`, 'gi'));
    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === search.toLowerCase() ? (
            <mark key={index} className="search-highlight">
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </>
    );
  };

  return (
    <div className="container-fluid">
      <div className="mb-4">
        <h3>Field Name Search</h3>
        <p className="text-muted">
          Search for database field names (keys) across all API tables. Use this to find which tables contain specific fields.
        </p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <Form.Group className="mb-3">
            <Form.Label>Search Field Names</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter field name to search (e.g., 'project', 'engineer', 'date')..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading || !dataFetched}
            />
            <Form.Text className="text-muted">
              Search is case-insensitive and matches partial field names.
            </Form.Text>
          </Form.Group>

          {loading && (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading field data from APIs...</p>
            </div>
          )}

          {!loading && !dataFetched && (
            <div className="text-center py-3">
              <Button variant="primary" onClick={fetchFieldNames} size="lg">
                <i className="bi bi-download me-2"></i>
                Load Field Data
              </Button>
              <p className="text-muted mt-2 mb-0">Click to fetch field names from all API tables</p>
            </div>
          )}

          {dataFetched && !loading && (
            <div className="mt-3">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={fetchFieldNames}
              >
                <i className="bi bi-arrow-clockwise me-2"></i>
                Refresh Field Data
              </Button>
              
              {/* API Load Status Summary */}
              <div className="mt-3 p-3 bg-light border rounded">
                <h6 className="mb-2">Data Load Status</h6>
                <div className="d-flex gap-3 flex-wrap">
                  <div>
                    <Badge bg="success">{loadStats.successCount}</Badge>
                    <span className="ms-1 text-muted small">Loaded</span>
                  </div>
                  <div>
                    <Badge bg="danger">{loadStats.errorCount}</Badge>
                    <span className="ms-1 text-muted small">Errors</span>
                  </div>
                  <div>
                    <Badge bg="warning">{loadStats.emptyCount}</Badge>
                    <span className="ms-1 text-muted small">Empty</span>
                  </div>
                  <div>
                    <Badge bg="info">{loadStats.totalFields}</Badge>
                    <span className="ms-1 text-muted small">Total Fields</span>
                  </div>
                </div>
                
                {/* Show all API status details */}
                <details className="mt-2">
                  <summary className="text-muted small" style={{ cursor: 'pointer' }}>
                    View All API Status Details ({Object.keys(loadedApis).length} APIs)
                  </summary>
                  <div className="mt-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {Object.entries(loadedApis)
                      .sort(([a], [b]) => formatApiName(a).localeCompare(formatApiName(b)))
                      .map(([apiName, api]) => (
                        <div key={apiName} className="small mb-1 d-flex align-items-center">
                          <Badge 
                            bg={
                              api.status === 'success' ? 'success' : 
                              api.status === 'error' ? 'danger' : 
                              api.status === 'empty' ? 'warning' : 'secondary'
                            }
                            className="me-2"
                            style={{ minWidth: '60px', textAlign: 'center' }}
                          >
                            {api.status}
                          </Badge>
                          <strong className="me-2">{formatApiName(apiName)}</strong>
                          {api.status === 'success' && api.count !== undefined && (
                            <span className="text-muted">({api.count} fields)</span>
                          )}
                          {api.error && <span className="text-danger ms-2">- {api.error}</span>}
                        </div>
                      ))}
                  </div>
                </details>
              </div>
            </div>
          )}
        </div>
      </div>

      {dataFetched && searchTerm && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              Search Results
              <Badge bg="primary" className="ms-2">
                {filteredResults.length} field{filteredResults.length !== 1 ? 's' : ''} found
              </Badge>
            </h5>
          </div>
          <div className="card-body">
            {filteredResults.length === 0 ? (
              <Alert variant="info" className="mb-0">
                No fields found matching "{searchTerm}". Try a different search term.
              </Alert>
            ) : (
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th style={{ width: "30%" }}>Table</th>
                    <th style={{ width: "35%" }}>Field Name</th>
                    <th style={{ width: "35%" }}>Raw Field Name</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((field, index) => (
                    <tr key={`${field.apiName}-${field.fieldName}-${index}`}>
                      <td>{field.formattedApiName}</td>
                      <td>
                        {highlightText(field.formattedFieldName, searchTerm)}
                      </td>
                      <td>
                        <code>{highlightText(field.fieldName, searchTerm)}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .search-highlight {
          background-color: #fff3cd;
          padding: 0;
          font-weight: bold;
        }
      `}</style>
    </div>
  );
}

export default AdminFieldSearch;
